import * as React from "react";
import { FileText, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { FileChip } from "@/components/ui/file-chip";
import { cn } from "@/lib/utils";

export type DocumentStatus = "processing" | "ready" | "error";

export interface DocumentIdentityProps {
  filename: string;
  fileType?: string | null;
  versionNumber?: number | null;
  status?: DocumentStatus;
  /** Render at a compact or full scale. */
  size?: "sm" | "md";
  /** Show version badge when a version number is available. */
  showVersion?: boolean;
  className?: string;
}

const DocumentIdentity = React.forwardRef<HTMLSpanElement, DocumentIdentityProps>(
  ({ filename, fileType, versionNumber, status, size = "md", showVersion = true, className }, ref) => {
    const iconSize = size === "sm" ? "size-3.5" : "size-4";
    const textSize = size === "sm" ? "text-xs" : "text-sm";
    // All file types currently resolve to FileText; differentiated icons deferred to Phase 3.
    void fileType;
    const Icon = status === "processing" ? Loader2 : FileText;

    return (
      <span
        ref={ref}
        className={cn("inline-flex min-w-0 items-center gap-1.5", className)}
        aria-busy={status === "processing" || undefined}
        aria-label={status === "processing" ? `Processing ${filename}` : undefined}
      >
        <Icon
          className={cn(
            iconSize,
            "shrink-0",
            status === "processing" ? "animate-spin text-muted-foreground" : "text-primary"
          )}
          aria-hidden
        />

        <FileChip filename={filename} className={cn(textSize, "border-0 bg-transparent px-0 py-0")} />

        {showVersion && versionNumber != null ? (
          <Badge variant="muted" className="shrink-0">
            V{versionNumber}
          </Badge>
        ) : null}

        {status === "error" ? (
          <Badge variant="destructive" className="shrink-0">
            Error
          </Badge>
        ) : null}
      </span>
    );
  }
);
DocumentIdentity.displayName = "DocumentIdentity";

export { DocumentIdentity };
